import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isPast } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { ANY_SPORT_MY_CARDS_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useRetainedValue } from '@sorare/core/src/hooks/useRetainedValue';

import { UnsealCardDialogWithFetch } from '#src/components/sealing/UnsealCardDialog/index.withFetch.tsx';
import { UnsealingAnimationWithFetch } from '#src/components/sealing/UnsealingAnimation/index.withFetch.tsx';
import { UnsealingCountdownDialog } from '#src/components/sealing/UnsealingCountdownDialog/index.tsx';

import type { TokenUnsealButton_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  item: TokenUnsealButton_anyCard;
};

export const TokenUnsealButton = ({ item }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const isUnsealable =
    item.sealed && item.unsealableAt && isPast(item.unsealableAt!);
  const [open, setOpen] = useState(false);
  const [openPostUnsealingAnimation, setOpenPostUnsealingAnimation] =
    useState(false);
  const [openCountdownDialog, setOpenCountdownDialog] = useState(false);
  const retainedUnsealableAt = useRetainedValue(item.unsealableAt ?? null);

  return (
    <>
      {!!item.sealed && (
        <>
          <IconButton
            onClick={() => {
              if (isUnsealable) {
                setOpen(true);
              } else {
                setOpenCountdownDialog(true);
              }
            }}
            color="secondary"
            size="small"
          >
            <Vault size={2} action="unvault" />
          </IconButton>
          <UnsealingCountdownDialog
            open={
              !!openCountdownDialog && !!retainedUnsealableAt && !isUnsealable
            }
            date={retainedUnsealableAt ?? new Date()}
            onClose={() => setOpenCountdownDialog(false)}
          />
          <UnsealCardDialogWithFetch
            open={open}
            rarity={item.rarityTyped}
            selectedCardsSlug={item.slug}
            onClose={() => setOpen(false)}
            onConfirm={async () => {
              setOpen(false);
              setOpenPostUnsealingAnimation(true);
            }}
          />
        </>
      )}
      {!!openPostUnsealingAnimation && (
        <UnsealingAnimationWithFetch
          rarity={item.rarityTyped}
          selectedCardsSlugs={[item.slug]}
          onClose={() => {
            setOpenPostUnsealingAnimation(false);
            navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS));
          }}
        />
      )}
    </>
  );
};

TokenUnsealButton.fragments = {
  anyCard: gql`
    fragment TokenUnsealButton_anyCard on AnyCardInterface {
      slug
      sealed
      unsealableAt
      rarityTyped
    }
  ` as TypedDocumentNode<TokenUnsealButton_anyCard>,
};
